import { useCallback, useEffect, useId, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// useKidCardGrants — pending card-grant queue for the kid mode reveal
// overlay. Two responsibilities:
//
//   1. On mount, fetch get_pending_kid_card_grants — the kid's
//      revealed_at IS NULL queue, oldest-first. This is what mounts
//      the reveal overlay on next-login.
//
//   2. Subscribe to realtime INSERTs on kid_card_grants for this kid
//      so a parent granting mid-session triggers the overlay without
//      a poll. We ignore UPDATE events (the only UPDATE is claim,
//      which is initiated by us — the optimistic dequeue is already
//      correct).
//
// claim(grantId) calls claim_kid_card_grant RPC and optimistically
// dequeues the grant locally so the overlay unmounts the moment the
// user taps COLLECT. On error, we re-fetch the queue rather than
// trying to surgically restore — RPC failure here is rare and a
// full re-fetch is correct in every scenario.
//
// Channel topic includes useId() in addition to safeChannel's dedup
// guard so two simultaneous mounts in dev HMR don't fight over the
// same topic (see Supabase realtime channel-uniqueness memory).

export function useKidCardGrants(kidUserId) {
  const [pendingGrants, setPendingGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);
  const reactId = useId();

  const load = useCallback(async () => {
    if (!kidUserId) {
      setPendingGrants([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: e } = await supabase.rpc("get_pending_kid_card_grants", {
      p_kid_user_id: kidUserId,
    });
    if (e) {
      console.error("[kid-card-grants] load:", e);
      setError(e);
      setLoading(false);
      return;
    }
    setPendingGrants(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [kidUserId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!kidUserId) return undefined;
    const ch = safeChannel(`rt:kid_card_grants:${kidUserId}:${reactId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "kid_card_grants",
          filter: `kid_user_id=eq.${kidUserId}`,
        },
        (payload) => {
          const row = payload?.new;
          // Defensive: realtime row may not include the alias mapping
          // get_pending_kid_card_grants does, but the column shape is
          // a superset — only push if it's an unrevealed grant.
          if (!row || row.revealed_at) return;
          setPendingGrants((prev) => {
            if (prev.some((g) => g.id === row.id)) return prev;
            const next = [...prev, {
              id: row.id,
              card_type: row.card_type,
              count: row.count,
              granted_by: row.granted_by,
              granted_at: row.granted_at,
            }];
            // Oldest-first to match the RPC ordering.
            next.sort((a, b) => new Date(a.granted_at) - new Date(b.granted_at));
            return next;
          });
        },
      )
      .subscribe();
    channelRef.current = ch;
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [kidUserId, reactId]);

  const claim = useCallback(async (grantId) => {
    if (!grantId || !kidUserId) return null;
    // Optimistic dequeue — overlay unmounts immediately on tap.
    setPendingGrants((prev) => prev.filter((g) => g.id !== grantId));
    const { data, error: e } = await supabase.rpc("claim_kid_card_grant", {
      p_kid_user_id: kidUserId,
      p_grant_id:    grantId,
    });
    if (e) {
      console.warn("[kid-card-grants] claim failed:", e.message);
      // Full re-fetch on failure is the safe move; the grant may
      // still be unrevealed server-side.
      load();
      return null;
    }
    // The kid_pet_state UPDATE inside the RPC fires the existing
    // useKidPetState realtime sub, so the deck balance refreshes
    // without a second fetch here.
    return data;
  }, [kidUserId, load]);

  return { pendingGrants, loading, error, claim, refresh: load };
}
